/**
 * RealtimeProvider
 *
 * Provides realtime subscription capabilities and automatic
 * react-query cache invalidation when database changes occur.
 */

import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRealtime, type PostgresChangesPayload } from '../hooks/useRealtime';

interface RealtimeContextValue {
  isConnected: boolean;
  error: string | null;
}

const RealtimeContext = createContext<RealtimeContextValue>({
  isConnected: false,
  error: null,
});

export function useRealtimeConnection() {
  return useContext(RealtimeContext);
}

// Map of table names to query keys that should be invalidated
const TABLE_TO_QUERY_KEYS: Record<string, string[]> = {
  claims: ['claims', 'feed', 'story'],
  stories: ['stories', 'feed', 'story'],
  materials: ['materials', 'feed'],
  sources: ['sources'],
  streams: ['streams'],
  topics: ['topics'],
  rundowns: ['rundowns', 'rundown'],
  'rundown-items': ['rundown'],
  'rundown-sections': ['rundown'],
  'rundown-claims': ['rundown'],
  quotes: ['quotes', 'feed'],
};

interface RealtimeProviderProps {
  children: ReactNode;
}

export function RealtimeProvider({ children }: RealtimeProviderProps) {
  const queryClient = useQueryClient();
  const { isConnected, error, subscribeToChanges } = useRealtime();

  useEffect(() => {
    // Subscribe to all table changes
    const unsubscribe = subscribeToChanges(
      { event: '*' }, // Listen to all events
      (payload: PostgresChangesPayload) => {
        const table = payload.table;
        const queryKeys = TABLE_TO_QUERY_KEYS[table];

        if (queryKeys) {
          // Invalidate all related queries
          queryKeys.forEach((key) => {
            queryClient.invalidateQueries({ queryKey: [key] });
          });

          console.log(`[Realtime] ${payload.eventType} on ${table} - invalidated:`, queryKeys);
        } else {
          // For unknown tables, try to invalidate by table name
          queryClient.invalidateQueries({ queryKey: [table] });
          console.log(`[Realtime] ${payload.eventType} on ${table} - invalidated:`, [table]);
        }
      }
    );

    return unsubscribe;
  }, [subscribeToChanges, queryClient]);

  return (
    <RealtimeContext.Provider value={{ isConnected, error }}>
      {children}
    </RealtimeContext.Provider>
  );
}

export default RealtimeProvider;
